import { useEffect } from 'react';
import { CallButton } from '@/components/CallButton';
import { Icon } from '@/components/Icon';
import { UrlForm } from '@/components/UrlForm';
import { FaqList } from '@/components/landing/FaqList';
import { track } from '@/lib/analytics';
import { NEVERZERO_PRICE_LABEL } from '@/lib/pricing';
import { useLang, useT } from '@/lib/useLang';

/**
 * Pour l'agence ou le ghostwriter qui gère LinkedIn pour d'autres : revendre
 * NeverZero sous sa marque. Le produit est exactement le même — un abonnement
 * par profil, payé avec l'email de l'agence — et la page ne promet rien de
 * plus que ce que le provisioning fait déjà (voir `WhiteLabelCopy`).
 *
 * Chaque formulaire porte son propre `id` : le suivi du checkout distingue
 * ainsi une agence d'un fondateur venu de la landing.
 */
export function WhiteLabel() {
  const t = useT();
  const lang = useLang();
  const wl = t.whiteLabel;

  useEffect(() => {
    track('white_label_page_viewed', { lang });
    // Une seule lecture à l'arrivée ; `lang` ne change pas sans navigation.
  }, []);

  return (
    <>
      <section id="top" className="nz-hero">
        <div className="nz-hero__panel">
          <div className="nz-hero__copy">
            <div className="nz-pill nz-pill--dark">
              <Icon name="users" className="nz-pill__icon" />
              {wl.badge}
            </div>
            <h1 className="nz-hero__h1">
              {wl.h1a} <span>{wl.h1b}</span>
            </h1>
            <p className="nz-hero__p1">{wl.lead}</p>

            <UrlForm id="white-label-hero-linkedin-url" />

            <div className="nz-hero__actions">
              <CallButton placement="white-label" tone="dark" />
            </div>

            <ul className="nz-hero__checks">
              {wl.checks.map((item) => (
                <li key={item}>
                  <Icon name="check" className="nz-hero__check" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="nz-wl-split">
            <SplitColumn title={wl.splitAgencyTitle} items={wl.splitAgency} />
            <SplitColumn title={wl.splitClientTitle} items={wl.splitClient} client />
          </div>
        </div>
      </section>

      <section id="method" className="nz-how">
        <div className="nz-how__panel">
          <div className="nz-eyebrow">{wl.howEyebrow}</div>
          <h2 className="nz-h2">{wl.howH2}</h2>
          <ol className="nz-how__steps">
            {wl.steps.map((step) => (
              <li key={step.n}>
                <div className="nz-step__n">{step.n}</div>
                <div className="nz-step__title">{step.title}</div>
                <div className="nz-step__body">{step.body}</div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section id="white-label" className="nz-section">
        <div className="nz-eyebrow">{wl.invisibleEyebrow}</div>
        <h2 className="nz-h2">{wl.invisibleH2}</h2>
        <p className="nz-lead">{wl.invisibleP}</p>

        <div className="nz-focus__grid">
          {wl.invisibleItems.map((item) => (
            <div className="nz-focus__item" key={item.title}>
              <div className="nz-focus__mark">
                <Icon name={item.icon} className="nz-focus__icon" />
              </div>
              <div className="nz-focus__title">{item.title}</div>
              <div className="nz-focus__body">{item.body}</div>
            </div>
          ))}
        </div>
      </section>

      <section id="pricing" className="nz-section">
        <div className="nz-pricing__card">
          <div className="nz-eyebrow">{wl.pricingEyebrow}</div>
          <h2 className="nz-h2">{wl.pricingH2}</h2>
          <div>
            <span className="nz-pricing__price">{NEVERZERO_PRICE_LABEL}</span>
            <span className="nz-pricing__per">{wl.pricingPer}</span>
          </div>
          <ul className="nz-pricing__list">
            {wl.pricingItems.map((item) => (
              <li key={item}>
                <span className="nz-trust__check" aria-hidden="true">
                  ✓
                </span>
                {item}
              </li>
            ))}
          </ul>
          <p className="nz-pricing__math">{wl.pricingMargin}</p>
          <UrlForm id="white-label-pricing-linkedin-url" />
        </div>

        <div className="nz-wl-volume">
          <div>
            <b>{wl.volumeTitle}</b>
            <p>{wl.volumeBody}</p>
          </div>
          <CallButton placement="white-label" />
        </div>
      </section>

      <section id="faq" className="nz-section">
        <div className="nz-eyebrow">{wl.faqEyebrow}</div>
        <h2 className="nz-h2">{wl.faqH2}</h2>
        <div className="nz-wl-faq">
          <FaqList items={wl.faqs} idPrefix="nz-wl-faq" openFirst />
        </div>
      </section>

      <section id="cta" className="nz-cta nz-cta--dark">
        <div className="nz-cta__panel">
          <svg className="nz-cta__arcs" viewBox="0 0 400 400" aria-hidden="true" focusable="false">
            <circle cx="400" cy="0" r="140" />
            <circle cx="400" cy="0" r="220" />
            <circle cx="400" cy="0" r="300" />
          </svg>

          <div className="nz-cta__copy">
            <h2 className="nz-cta__h2">{wl.ctaH2}</h2>
            <p className="nz-cta__p">{wl.ctaP}</p>
            <div className="nz-cta__note">{t.ctaNote}</div>
          </div>

          <div className="nz-cta__form">
            <UrlForm id="white-label-cta-linkedin-url" />
          </div>
        </div>
      </section>
    </>
  );
}

/** Qui fait quoi : l'agence d'un côté, son client de l'autre. */
function SplitColumn({ title, items, client = false }: { title: string; items: string[]; client?: boolean }) {
  return (
    <div className={`nz-wl-split__col${client ? ' nz-wl-split__col--client' : ''}`}>
      <div className="nz-wl-split__title">{title}</div>
      <ul>
        {items.map((item) => (
          <li key={item}>
            <Icon name="check" className="nz-wl-split__check" />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}
